import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AuditEntry, TenantSummary } from '@order-pilot/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { IconBook, IconChevronLeft, IconChevronRight } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import {
  AuditFilters,
  EMPTY_AUDIT_FILTERS,
  auditKeys,
  fetchAuditActions,
  fetchAuditEntries,
} from '@/features/audit/api';
import { fetchTenants, tenantKeys } from '@/features/tenants/api';
import { formatDateTime } from '@/lib/format';

const PAGE_SIZE = 25;

/** Enough tenants for the dropdown to cover a realistic platform. */
const TENANT_OPTION_LIMIT = 100;

const ACTOR_KINDS = ['SA', 'TENANT', 'DEVICE', 'SYSTEM'] as const;

const ACTOR_TONE: Record<string, BadgeTone> = {
  SA: 'accent',
  TENANT: 'success',
  DEVICE: 'neutral',
  SYSTEM: 'neutral',
};

/**
 * Meta is free-form JSON. It is shown compactly so a row stays one line, and
 * expandable because that object is the whole point of an audit entry when
 * something has to be reconstructed.
 */
function MetaCell({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const keys = Object.keys(entry.meta);
  if (keys.length === 0) return <span className="text-slate-300">—</span>;

  const compact = JSON.stringify(entry.meta);

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="max-w-full rounded text-left font-mono text-xs text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
        title={open ? 'Collapse' : 'Expand'}
      >
        {open ? (
          <pre className="max-w-md overflow-x-auto whitespace-pre-wrap break-all">
            {JSON.stringify(entry.meta, null, 2)}
          </pre>
        ) : (
          <span className="block max-w-xs truncate">{compact}</span>
        )}
      </button>
    </div>
  );
}

export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [page, setPage] = useState(1);

  // Any filter change starts again at page 1: page 4 of a narrower result set is
  // usually empty, which reads as "no matches".
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const params = { ...filters, page, pageSize: PAGE_SIZE };
  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: auditKeys.list(params),
    queryFn: ({ signal }) => fetchAuditEntries(params, signal),
    placeholderData: keepPreviousData,
  });

  const actionsQuery = useQuery({
    queryKey: auditKeys.actions(),
    queryFn: ({ signal }) => fetchAuditActions(signal),
    staleTime: 5 * 60_000,
  });

  const tenantsQuery = useQuery({
    queryKey: tenantKeys.list({ page: 1, pageSize: TENANT_OPTION_LIMIT, search: '' }),
    queryFn: ({ signal }) =>
      fetchTenants({ page: 1, pageSize: TENANT_OPTION_LIMIT, search: '' }, signal),
    staleTime: 5 * 60_000,
  });

  const tenantsById = new Map<string, TenantSummary>(
    (tenantsQuery.data?.items ?? []).map((tenant) => [tenant.id, tenant]),
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const filtered = Object.values(filters).some((value) => value !== '');

  function update<K extends keyof AuditFilters>(key: K, value: AuditFilters[K]): void {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every privileged action on the platform: logins, tenant and device changes, impersonation."
      />

      <div className="card overflow-hidden">
        <div className="grid gap-3 border-b border-slate-200 px-4 py-3.5 sm:grid-cols-2 sm:px-5 lg:grid-cols-5">
          <div>
            <label htmlFor="audit-tenant" className="label mb-1 text-xs">
              Tenant
            </label>
            <select
              id="audit-tenant"
              className="input h-9 py-0 pr-8 text-sm"
              value={filters.tenantId}
              onChange={(event) => update('tenantId', event.target.value)}
            >
              <option value="">All tenants</option>
              {(tenantsQuery.data?.items ?? []).map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="audit-action" className="label mb-1 text-xs">
              Action
            </label>
            <select
              id="audit-action"
              className="input h-9 py-0 pr-8 text-sm"
              value={filters.action}
              onChange={(event) => update('action', event.target.value)}
            >
              <option value="">All actions</option>
              {(actionsQuery.data ?? []).map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="audit-actor" className="label mb-1 text-xs">
              Actor
            </label>
            <select
              id="audit-actor"
              className="input h-9 py-0 pr-8 text-sm"
              value={filters.actorKind}
              onChange={(event) => update('actorKind', event.target.value)}
            >
              <option value="">Any actor</option>
              {ACTOR_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="audit-from" className="label mb-1 text-xs">
              From
            </label>
            <input
              id="audit-from"
              type="date"
              className="input h-9 py-0 text-sm"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(event) => update('from', event.target.value)}
            />
          </div>

          <div>
            <label htmlFor="audit-to" className="label mb-1 text-xs">
              To
            </label>
            <input
              id="audit-to"
              type="date"
              className="input h-9 py-0 text-sm"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(event) => update('to', event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-surface/60 px-4 py-2.5 sm:px-5">
          <p className="text-xs text-slate-500" aria-live="polite">
            {isPending ? 'Loading…' : `${total.toLocaleString()} entr${total === 1 ? 'y' : 'ies'}`}
            {!isPending && isFetching ? ' · refreshing' : ''}
          </p>
          {filtered ? (
            <Button size="sm" onClick={() => setFilters(EMPTY_AUDIT_FILTERS)}>
              Clear filters
            </Button>
          ) : null}
        </div>

        {isPending ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4 px-5 py-3.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="hidden h-3.5 w-32 md:block" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            error={error}
            title="Could not load the audit log"
            onRetry={() => void refetch()}
            retrying={isFetching}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<IconBook className="h-5 w-5" />}
            title={filtered ? 'No entries match these filters' : 'Nothing audited yet'}
            description={
              filtered
                ? 'Try a wider date range, or clear the filters to see every entry.'
                : 'Privileged actions appear here as they happen.'
            }
            action={
              filtered ? (
                <Button onClick={() => setFilters(EMPTY_AUDIT_FILTERS)}>Clear filters</Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Audit entries, newest first</caption>
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th">
                    When
                  </th>
                  <th scope="col" className="th">
                    Actor
                  </th>
                  <th scope="col" className="th">
                    Action
                  </th>
                  <th scope="col" className="th">
                    Tenant
                  </th>
                  <th scope="col" className="th">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((entry) => {
                  const tenant = entry.tenantId ? tenantsById.get(entry.tenantId) : undefined;
                  return (
                    <tr key={entry.id}>
                      <td className="td whitespace-nowrap text-slate-600" title={entry.at}>
                        {formatDateTime(entry.at)}
                      </td>
                      <td className="td">
                        <Badge tone={ACTOR_TONE[entry.actorKind] ?? 'neutral'}>
                          {entry.actorKind}
                        </Badge>
                      </td>
                      <td className="td">
                        <code className="font-mono text-xs text-slate-900">{entry.action}</code>
                      </td>
                      <td className="td">
                        {entry.tenantId === null ? (
                          <span className="text-slate-400">Platform</span>
                        ) : (
                          <Link
                            to={`/cp/tenants/${entry.tenantId}`}
                            className="rounded text-slate-700 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                            title={entry.tenantId}
                          >
                            {tenant?.name ?? `${entry.tenantId.slice(0, 8)}…`}
                          </Link>
                        )}
                      </td>
                      <td className="td">
                        <MetaCell entry={entry} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isPending && !isError && items.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3.5 sm:px-5">
            <p className="text-xs text-slate-500 tabular-nums">
              Showing {rangeStart}–{rangeEnd} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                icon={<IconChevronLeft className="h-3.5 w-3.5" />}
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <span className="px-1 text-xs text-slate-500 tabular-nums">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
                <IconChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
